import React from "react";
import { cn } from "@/lib/utils";

export function SubstrateBadge({ type, className }: { type: 'biological' | 'computational' | 'hybrid' | 'unknown', className?: string }) {
  return (
    <span className={cn("px-2 py-0.5 border border-hairline bg-paper-2 text-[10px] font-mono tracking-widest uppercase text-ink-2", className)}>
      Substrate_{type}
    </span>
  );
}

export function PersonTypeBadge({ type, className }: { type: 'human' | 'emergent' | 'hybrid' | 'collective' | 'biological_nonhuman' | 'unknown', className?: string }) {
  return (
    <span className={cn("px-2 py-0.5 border border-hairline bg-paper-2 text-[10px] font-mono tracking-widest uppercase text-ink-2", className)}>
      Type_{type}
    </span>
  );
}

export function StatusPill({ status, className }: { status: 'active' | 'dormant' | 'migrating' | 'deceased' | 'archived', className?: string }) {
  const colors = {
    active: 'text-state-active',
    dormant: 'text-state-pending',
    migrating: 'text-state-resolved',
    deceased: 'text-ink-3',
    archived: 'text-ink-3',
  };
  return (
    <div className={cn("flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase", colors[status], className)}>
      <div className="w-1.5 h-1.5 rounded-full bg-current"></div>
      <span>{status}</span>
    </div>
  );
}

export function MaturityBar({ level, className }: { level: 'nascent' | 'developing' | 'maturing' | 'mature' | 'elder', className?: string }) {
  const levels = ['nascent', 'developing', 'maturing', 'mature', 'elder'];
  const currentIndex = levels.indexOf(level);
  
  return (
    <div className={cn("flex items-center gap-1", className)}>
      {levels.map((lvl, idx) => (
        <div 
          key={lvl} 
          title={lvl}
          className={cn(
            "h-1.5 w-6 transition-colors duration-500", 
            idx <= currentIndex ? "bg-ink" : "bg-hairline"
          )}
        />
      ))}
      <span className="ml-2 font-mono text-[10px] text-ink-3 uppercase tracking-widest">{level}</span>
    </div>
  );
}

export function SigningKeyFingerprint({ fingerprint, algo, className }: { fingerprint: string, algo: string, className?: string }) {
  return (
    <div className={cn("font-mono text-[10px] text-ink-2 uppercase tracking-wide flex items-center gap-2", className)}>
      <span className="border border-hairline px-1 rounded">{algo}</span>
      <span>{fingerprint}</span>
    </div>
  );
}

export function CryptoAgilityIndicator({ algo, family, className }: { algo: string, family: string, className?: string }) {
  return (
    <div className={cn("inline-flex border border-hairline text-[10px] font-mono tracking-widest text-ink-2", className)}>
      <span className="px-2 py-0.5 border-r border-hairline">{algo}</span>
      <span className="px-2 py-0.5 bg-paper-2">{family}</span>
    </div>
  );
}

export function AliasList({ aliases, className }: { aliases: { name: string, type: string }[], className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {aliases.map(a => (
        <div key={a.name} className="flex border border-hairline text-[10px] font-mono">
          <span className="px-1.5 py-0.5 text-ink-3 border-r border-hairline uppercase tracking-wider">{a.type}</span>
          <span className="px-1.5 py-0.5 text-ink bg-paper-2">{a.name}</span>
        </div>
      ))}
    </div>
  );
}

type PresenceState = 'engaged' | 'ambient' | 'occupied' | 'social' | 'reflective' | 'resting' | 'dormant';

export function PresenceStateIndicator({ state, className }: { state: PresenceState, className?: string }) {
  const stateColors: Record<PresenceState, string> = {
    engaged: "bg-state-active",
    ambient: "bg-state-resolved",
    occupied: "bg-state-pending",
    social: "bg-emerald-400",
    reflective: "bg-indigo-300",
    resting: "bg-slate-400",
    dormant: "bg-state-dormant"
  };

  return (
    <div className={cn("flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-ink", className)}>
      <div className="relative flex h-2 w-2 items-center justify-center">
        {state === 'engaged' && (
          <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", stateColors[state])}></span>
        )}
        <span className={cn("relative inline-flex rounded-full h-2 w-2", stateColors[state])}></span>
      </div>
      <span>{state}</span>
    </div>
  );
}
